import { formatMajorMoney } from '@eventbrite/intl';
import { find } from 'lodash';
import { getTotalCost } from '../../containers/ticketSelection/utils/getTotalCost';
import { getTotalDisplayCost } from '../../containers/ticketSelection/utils/ticketPriceUtils';
import { CheckoutState } from '../../state';
import { EventNamespace } from '../../state/event';
import { getSelectedDeliveryMethodsForDisplay } from '../deliveryMethod';
import { getTotalCostsFromGrossValue } from '../order';
import { hasExistingOrder } from '../order/typedOrderSelectors';

/**
 * Get total cost and shipping fees of the selected tickets or order
 */
export const getTicketsCostDetails = (state: CheckoutState) => {
    const {
        tickets: { ticketsById },
        event: { shippingRates },
    } = state;

    const baseCostInCents =
        hasExistingOrder(state) || getTotalCostsFromGrossValue(state) > 0
            ? getTotalCostsFromGrossValue(state)
            : getTotalCost(ticketsById);

    const selectedDeliveryMethods = getSelectedDeliveryMethodsForDisplay(state);

    const shippingFeeInCents = selectedDeliveryMethods.reduce(
        (previousShippingFee: number, deliveryMethod: string) => {
            const singleShippingRate = find(
                shippingRates,
                (rate) => rate.name === deliveryMethod,
            );

            return singleShippingRate
                ? previousShippingFee + singleShippingRate.price.value
                : previousShippingFee;
        },
        0,
    );

    const totalCostInCents = baseCostInCents + shippingFeeInCents;

    return { totalCostInCents, shippingFeeInCents };
};

/**
 * Get common display props used by OrderSummary and Footer from the
 * selected tickets, using event currency and app currency format
 */
export const getDisplayCostProps = (
    state: CheckoutState & {
        event: EventNamespace;
    },
) => {
    const {
        tickets: { ticketsById },
        event: { currency },
        app: { currencyFormat },
    } = state;

    const { totalCostInCents, shippingFeeInCents } =
        getTicketsCostDetails(state);

    return {
        currency,
        totalCostUnformatted: totalCostInCents,
        totalCost: formatMajorMoney(totalCostInCents, currency, currencyFormat),
        totalCostDisplay: getTotalDisplayCost(
            ticketsById,
            currency,
            currencyFormat,
            shippingFeeInCents,
        ),
    };
};
